import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Plus, Search, Loader2, FileText, Trash2, Pencil, ExternalLink,
  ChevronLeft, ChevronRight, ArrowUp, ArrowDown, X,
} from 'lucide-react';
import { API } from '../../lib/api';
import { notifyErr } from '../../components/admin/billing/shared';

const fmtDate = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString(); } catch { return s; }
};

const STATUS_OPTIONS = ['draft', 'in_progress', 'completed', 'cancelled'];

const SortHeader = ({ colKey, sortBy, sortDir, onToggle, align = 'left', children }) => (
  <th className={`px-4 py-3 ${align === 'right' ? 'text-right' : ''}`}>
    <button
      type="button"
      onClick={() => onToggle(colKey)}
      className="inline-flex items-center gap-1 uppercase tracking-wider text-xs text-slate-400 hover:text-slate-200"
      data-testid={`sort-${colKey}`}
    >
      {children}
      {sortBy === colKey && (sortDir === 'asc' ? <ArrowUp size={11} /> : <ArrowDown size={11} />)}
    </button>
  </th>
);

const Inspections = () => {
  const { authHeader } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [unbilledOnly, setUnbilledOnly] = useState(false);
  const [status, setStatus] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [error, setError] = useState('');

  // Debounce the search box so we don't hammer the API on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Reset to page 1 whenever any filter changes.
  useEffect(() => { setPage(1); },
    [debouncedSearch, unbilledOnly, status, fromDate, toDate, sortBy, sortDir, pageSize]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        page, page_size: pageSize, sort_by: sortBy, sort_dir: sortDir,
      };
      if (debouncedSearch) params.q = debouncedSearch;
      if (unbilledOnly) params.unbilled = true;
      if (status) params.status = status;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const { data } = await axios.get(`${API}/inspections`, { params, headers: authHeader() });
      // Backend now returns { rows, total, page, pages }; support legacy array
      // response too (in case a stale worker is still serving the old shape).
      const rows = Array.isArray(data) ? data : (data?.rows || []);
      setItems(rows);
      setTotal(Array.isArray(data) ? rows.length : (data?.total ?? rows.length));
      setPages(Array.isArray(data) ? 1 : (data?.pages ?? 1));
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [authHeader, debouncedSearch, unbilledOnly, status, fromDate, toDate,
      sortBy, sortDir, page, pageSize]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const onDelete = async (id, name) => {
    if (!window.confirm(`Delete inspection for "${name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/inspections/${id}`, { headers: authHeader() });
      fetchAll();
    } catch (e) {
      notifyErr(e);
    }
  };

  const toggleSort = (key) => {
    if (sortBy === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir('desc');
    }
  };

  const clearFilters = () => {
    setSearch(''); setStatus(''); setFromDate(''); setToDate('');
    setUnbilledOnly(false); setSortBy('created_at'); setSortDir('desc');
  };

  const filtersActive = !!(debouncedSearch || status || fromDate || toDate || unbilledOnly);
  const startIdx = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const endIdx = Math.min(page * pageSize, total);

  return (
    <div className="min-h-screen">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6 lg:py-8">
        <div className="flex items-center justify-between gap-3 mb-4 sm:mb-6">
          <div className="hidden lg:flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-lg bg-teal-500/15 border border-teal-500/30 flex items-center justify-center flex-shrink-0">
              <FileText className="text-teal-300" size={18} />
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl font-semibold tracking-tight" data-testid="inspections-title">Inspection &amp; Delivery</h1>
              <p className="text-sm text-slate-500 mt-0.5">Vehicle inspection forms · PDF · WhatsApp share</p>
            </div>
          </div>
          <div className="lg:hidden" />
          <button
            onClick={() => navigate('/admin/inspections/new')}
            className="inline-flex items-center gap-1.5 bg-teal-500 hover:bg-teal-400 text-slate-950 font-semibold text-sm px-3 sm:px-4 py-2 rounded-lg transition flex-shrink-0 ml-auto"
            data-testid="new-inspection-btn"
          >
            <Plus size={16} /> <span className="hidden xs:inline">New </span>Inspection
          </button>
        </div>

        {/* Filters row 1: search + unbilled + clear */}
        <div className="flex flex-col sm:flex-row gap-3 mb-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by reg. number, customer, phone, email, make, model, inspector, sl. no…"
              className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-teal-400 focus:ring-1 focus:ring-teal-400"
              data-testid="inspections-search"
            />
          </div>
          <label className="inline-flex items-center gap-2 px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-sm text-slate-200 cursor-pointer hover:bg-white/10 transition"
                 data-testid="unbilled-toggle">
            <input
              type="checkbox"
              checked={unbilledOnly}
              onChange={(e) => setUnbilledOnly(e.target.checked)}
              className="h-4 w-4 accent-teal-500"
            />
            <span className="whitespace-nowrap">Unbilled only</span>
          </label>
          {filtersActive && (
            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex items-center gap-1 px-3 py-2.5 bg-white/5 border border-white/10 hover:bg-white/10 rounded-lg text-sm text-slate-300"
              data-testid="clear-filters"
            >
              <X size={13} /> Clear
            </button>
          )}
        </div>

        {/* Filters row 2: date range + status + page size */}
        <div className="flex flex-col sm:flex-row flex-wrap gap-3 mb-5">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-slate-500">From</span>
            <input
              type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
              className="bg-white/5 border border-white/10 rounded-lg px-2 py-2 text-sm text-slate-200"
              data-testid="from-date"
            />
            <span className="text-slate-500">To</span>
            <input
              type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
              className="bg-white/5 border border-white/10 rounded-lg px-2 py-2 text-sm text-slate-200"
              data-testid="to-date"
            />
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-xs uppercase tracking-wider text-slate-500 mr-1">Status</span>
            <button
              type="button"
              onClick={() => setStatus('')}
              className={`text-xs px-2.5 py-1 rounded-full border transition ${status === '' ? 'bg-teal-500/20 border-teal-400 text-teal-200' : 'bg-white/5 border-white/10 text-slate-400 hover:text-slate-200'}`}
              data-testid="status-all"
            >All</button>
            {STATUS_OPTIONS.map((s) => (
              <button key={s} type="button"
                onClick={() => setStatus(s === status ? '' : s)}
                className={`text-xs px-2.5 py-1 rounded-full border capitalize transition ${status === s ? 'bg-teal-500/20 border-teal-400 text-teal-200' : 'bg-white/5 border-white/10 text-slate-400 hover:text-slate-200'}`}
                data-testid={`status-${s}`}
              >{s.replace('_', ' ')}</button>
            ))}
          </div>
          <div className="ml-auto flex items-center gap-2 text-sm text-slate-400">
            <span>Rows per page:</span>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="bg-white/5 border border-white/10 rounded px-2 py-1 text-sm"
              data-testid="page-size"
            >
              {[25, 50, 100, 200].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </div>

        {error && (
          <div className="mb-4 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" data-testid="inspections-error">
            {String(error)}
          </div>
        )}

        <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/5 border-b border-white/10">
                <tr className="text-left">
                  <SortHeader colKey="created_at" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort}>Date</SortHeader>
                  <SortHeader colKey="serial_no" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort}>Sl. No.</SortHeader>
                  <SortHeader colKey="customer_name" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort}>Customer</SortHeader>
                  <SortHeader colKey="make" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort}>Vehicle</SortHeader>
                  <th className="px-4 py-3 text-xs uppercase tracking-wider text-slate-400">Phone</th>
                  <SortHeader colKey="status" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort}>Status</SortHeader>
                  <th className="px-4 py-3 text-xs uppercase tracking-wider text-slate-400 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-400" data-testid="inspections-loading">
                    <Loader2 className="animate-spin inline mr-2" size={16} /> Loading…
                  </td></tr>
                )}
                {!loading && items.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-400" data-testid="inspections-empty">
                    {filtersActive
                      ? <>No inspections match these filters. <button onClick={clearFilters} className="text-teal-300 underline">Clear filters</button>.</>
                      : <>No inspections yet. Click <strong className="text-teal-300">New Inspection</strong> to create one.</>}
                  </td></tr>
                )}
                {!loading && items.map((it) => (
                  <tr key={it.id} className="border-b border-white/5 hover:bg-white/[0.03]" data-testid={`inspection-row-${it.id}`}>
                    <td className="px-4 py-3 text-slate-400 whitespace-nowrap">{fmtDate(it.inspection_date || it.created_at)}</td>
                    <td className="px-4 py-3 text-slate-400 font-mono text-xs">{it.serial_no || '—'}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-100">{it.customer_name}</div>
                      {it.email && <div className="text-xs text-slate-500">{it.email}</div>}
                    </td>
                    <td className="px-4 py-3 text-slate-300">
                      {[it.make, it.model].filter(Boolean).join(' ') || '—'}
                      {it.vehicle_year && <span className="text-xs text-slate-500 ml-1">({it.vehicle_year})</span>}
                      {it.registration_number && (
                        <div className="text-xs font-mono tracking-wider text-teal-300 mt-0.5" data-testid="row-reg-number">
                          {it.registration_number}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-400 font-mono text-xs">{it.contact_number || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 text-xs border rounded px-2 py-0.5 capitalize ${
                        it.status === 'completed'
                          ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
                          : 'bg-amber-500/15 text-amber-300 border-amber-500/30'
                      }`} data-testid={`inspection-status-${it.id}`}>
                        {it.status || 'draft'}
                      </span>
                      {it.billed_invoice_id && (
                        <span
                          className="ml-1.5 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider border rounded px-1.5 py-0.5 bg-violet-500/15 text-violet-300 border-violet-500/30"
                          data-testid={`billed-pill-${it.id}`}
                          title="Already added to an invoice"
                        >
                          Billed
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1.5">
                        <button
                          onClick={() => navigate(`/admin/inspections/${it.id}`)}
                          className="inline-flex items-center gap-1 text-xs bg-teal-500/15 hover:bg-teal-500/25 border border-teal-500/30 text-teal-300 px-2.5 py-1.5 rounded transition"
                          data-testid={`open-inspection-${it.id}`}
                          title="Open / edit"
                        >
                          <Pencil size={12} /> Open
                        </button>
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              const res = await axios.get(`${API}/inspections/${it.id}/pdf`, {
                                headers: authHeader(), responseType: 'blob', timeout: 60000,
                              });
                              const blob = new Blob([res.data], { type: 'application/pdf' });
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = `Inspection_${(it.customer_name || 'form').replace(/\s+/g, '_')}_${it.id.slice(0, 8)}.pdf`;
                              a.rel = 'noopener';
                              document.body.appendChild(a);
                              a.click();
                              setTimeout(() => {
                                document.body.removeChild(a);
                                URL.revokeObjectURL(url);
                              }, 1000);
                            } catch (err) {
                              alert(err.response?.data?.detail || err.message || 'Failed to download PDF');
                            }
                          }}
                          className="inline-flex items-center gap-1 text-xs bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 px-2.5 py-1.5 rounded transition"
                          data-testid={`pdf-${it.id}`}
                          title="Download PDF"
                        >
                          <ExternalLink size={12} /> PDF
                        </button>
                        <button
                          onClick={() => onDelete(it.id, it.customer_name)}
                          className="inline-flex items-center justify-center w-8 h-8 bg-white/5 hover:bg-red-500/20 border border-white/10 hover:border-red-500/40 text-slate-400 hover:text-red-300 rounded transition"
                          data-testid={`delete-inspection-${it.id}`}
                          title="Delete"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination footer */}
          <div className="flex items-center justify-between px-4 py-3 border-t border-white/10 text-sm text-slate-400" data-testid="pagination">
            <div>
              {total === 0
                ? 'No results'
                : `${startIdx.toLocaleString()}–${endIdx.toLocaleString()} of ${total.toLocaleString()}`}
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="inline-flex items-center gap-1 px-2.5 py-1 bg-white/5 hover:bg-white/10 border border-white/10 rounded disabled:opacity-40"
                data-testid="page-prev"
              >
                <ChevronLeft size={14} /> Prev
              </button>
              <span className="px-3 text-slate-500">
                Page {page} of {pages || 1}
              </span>
              <button
                type="button"
                disabled={page >= pages || loading}
                onClick={() => setPage((p) => Math.min(pages, p + 1))}
                className="inline-flex items-center gap-1 px-2.5 py-1 bg-white/5 hover:bg-white/10 border border-white/10 rounded disabled:opacity-40"
                data-testid="page-next"
              >
                Next <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Inspections;
